import type { WebGLRenderer } from "three";
import { VOLUME_PREF } from "../../audio/audio-model.ts";
import { audio } from "../../audio/engine.ts";
import { animatedLimit } from "../../game/animated-set.ts";
import { blobLimit } from "../../render/blob-shadows.ts";
import { QUALITY } from "../../render/quality.ts";
import { CAPTIONS_PREF } from "../hud/captions.ts";
import { defaultSettings, parseSettings, pixelRatio, PRESETS, SETTINGS_PREF, type Graphics, type Settings } from "./settings-model.ts";
import { readPref, writePref } from "../kit/storage.ts";
import { keyLabel } from "../hud/tutorial-model.ts";
import type { Action } from "./settings-model.ts";
import { setColorblind } from "../../render/style/roles.ts";
import { applyClassTheme, rememberedClass } from "../theme/class-theme.ts";
import { applyPaletteTokens } from "../theme/tokens.ts";
import { halfFloatTargets } from "../../render/post/post-model.ts";

type Listener = (s: Settings) => void;

/**
 * Configurações em uso (F30): lidas uma vez do navegador, gravadas a cada mudança e aplicadas na
 * hora (volumes, qualidade gráfica, classes de acessibilidade); a cena assina para o resto (FOV,
 * teclas, mouse, tremor, legendas).
 */
class SettingsStore {
  private value: Settings = defaultSettings();
  private readonly listeners = new Set<Listener>();
  private renderer: WebGLRenderer | undefined;
  /** Preset escolhido pelo Automático nesta partida (não é gravado). */
  private auto: Exclude<Graphics["preset"], "automatico"> | undefined;
  /** Alvo em meio-float (F52: sem ele, nada de bloom), lido uma vez do renderer. */
  private float: boolean | undefined;

  load(): Settings {
    this.value = parseSettings(readPref(SETTINGS_PREF, ""), { volumes: readPref(VOLUME_PREF, ""), captions: readPref(CAPTIONS_PREF, "") });
    this.apply();
    return this.value;
  }

  get(): Settings {
    return this.value;
  }

  /** Muda, grava e aplica. */
  set(next: Settings): void {
    this.value = next;
    writePref(SETTINGS_PREF, JSON.stringify(next));
    this.apply();
    for (const l of this.listeners) l(next);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  attachRenderer(renderer: WebGLRenderer): void {
    this.renderer = renderer;
    this.float = undefined;
    this.apply();
    this.watchPixelRatio();
  }

  /**
   * A janela mudou de tela (Retina ↔ monitor externo, Gate D): o pixel ratio do aparelho muda e o canvas
   * segue, no teto do tier. A consulta vale para o DPR de agora; a cada troca, outra para o novo.
   */
  private watchPixelRatio(): void {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener(
      "change",
      () => {
        this.apply();
        this.watchPixelRatio();
      },
      { once: true },
    );
  }

  /** O navegador desenha em meio-float (F52: o bloom precisa; sem renderer, presume que sim). */
  floatTargets(): boolean {
    if (this.float === undefined && this.renderer) this.float = halfFloatTargets(this.renderer.extensions);
    return this.float ?? true;
  }

  /** O Automático mediu os primeiros 10 s: aplica o preset escolhido sem gravar. */
  setAuto(preset: Exclude<Graphics["preset"], "automatico"> | undefined): void {
    this.auto = preset;
    this.apply();
  }

  /** Preset que vale agora (no Automático, o medido, ou o Médio enquanto mede). */
  effectivePreset(): Exclude<Graphics["preset"], "automatico"> {
    const p = this.value.graphics.preset;
    return p === "automatico" ? (this.auto ?? "medio") : p;
  }

  /** Qualidade efetiva: no Automático, a escolhida pela medição (ou o Médio enquanto mede). */
  graphics(): Omit<Graphics, "preset"> {
    const g = this.value.graphics;
    if (g.preset === "automatico") return PRESETS[this.auto ?? "medio"];
    return g;
  }

  private apply(): void {
    const s = this.value;
    audio.setVolumes(s.volumes);
    const g = this.graphics();
    QUALITY.distance = g.distance;
    QUALITY.grass = g.grass / 100;
    // "Capim e detalhes" (F44): os enfeites do chão seguem o capim (Desligado esconde, Baixo corta metade).
    QUALITY.details = g.grass / 100;
    // Sombras-blob (F51) e criaturas animadas (F50): metade no Baixo.
    QUALITY.blobs = blobLimit(this.effectivePreset());
    QUALITY.animated = animatedLimit(this.effectivePreset());
    // Sombra do sol (Fase 13): a opção (o tamanho do mapa vem do tier; o Baixo nunca tem).
    QUALITY.shadows = g.shadows;
    // Pós-processamento (F52): o preset efetivo (o Automático escolhe sem avisar a cena) e as opções.
    QUALITY.post = { tier: this.effectivePreset(), bloom: g.bloom, grade: g.grade, vignette: g.vignette, light: g.light, aberration: g.aberration, flare: g.flare, motion: g.motion, reduceFlashes: s.reduceFlashes };
    if (this.renderer && typeof window !== "undefined") {
      const ratio = pixelRatio(window.devicePixelRatio, this.effectivePreset(), g.resolution);
      if (Math.abs(this.renderer.getPixelRatio() - ratio) > 1e-3) {
        this.renderer.setPixelRatio(ratio);
        // A cena refaz o tamanho do canvas no evento de redimensionar.
        window.dispatchEvent(new Event("resize"));
      }
    }
    if (typeof document !== "undefined") {
      document.body.classList.toggle("sem-lampejos", s.reduceFlashes);
      document.body.classList.toggle("daltonico", s.colorblind);
      // Modo daltônico (F60): a variante da paleta nas variáveis CSS.
      applyPaletteTokens(document.documentElement, s.colorblind);
    }
    // Modo daltônico (F60): os acentos do 3D também (os materiais vivos são reescritos).
    setColorblind(s.colorblind);
    // E o acento da UI, que é o da classe (F85; na partida, a cena recalcula o dela no quadro seguinte).
    if (typeof document !== "undefined" && document.body.dataset["tema"] === "classe") applyClassTheme(rememberedClass());
  }
}

export const settings = new SettingsStore();

/** Nome da tecla de uma ação, para os rótulos do HUD ("E abre" vira a tecla remapeada). */
export const keyName = (action: Action): string => keyLabel(settings.get().bindings[action][0] ?? "");

/**
 * Dica com tecla nos painéis ("E fecha"): o texto é refeito quando as teclas mudam. `{}` no modelo
 * vira o nome da tecla da ação.
 */
export function keyHint(action: Action, template: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "hint";
  p.dataset["tecla"] = action;
  p.dataset["modelo"] = template;
  p.textContent = template.replace("{}", keyName(action));
  return p;
}

export function refreshKeyHints(): void {
  for (const el of document.querySelectorAll<HTMLElement>("[data-tecla][data-modelo]")) {
    el.textContent = (el.dataset["modelo"] ?? "").replace("{}", keyName(el.dataset["tecla"] as Action));
  }
}
